import { apiUrl } from '../api-config';
import { escapeHtml } from './html';

export interface MaintenanceConfig {
  enabled: boolean;
  title?: string;
  message?: string;
}

export interface AnnouncementConfig {
  enabled: boolean;
  target?: 'broadcast' | 'view' | 'all';
  message?: string;
  severity?: 'info' | 'warning' | 'alert';
}

export interface StatusConfig {
  maintenance?: MaintenanceConfig;
  announcement?: AnnouncementConfig;
}

export function isMaintenanceBypassed(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('bypass_maintenance') === '1' || params.get('bypass_maintenance') === 'true') {
    window.sessionStorage.setItem('bypass_maintenance', 'true');
    window.localStorage.setItem('bypass_maintenance', 'true');
    return true;
  }
  return window.sessionStorage.getItem('bypass_maintenance') === 'true' ||
         window.localStorage.getItem('bypass_maintenance') === 'true';
}

/**
 * Loads the current status.json from the static host.
 * Bypasses cache with a timestamp query to ensure immediate updates.
 */
export async function fetchStatusConfig(): Promise<StatusConfig | null> {
  try {
    const res = await fetch(`/status.json?_=${Date.now()}`, {
      cache: 'no-cache',
    });
    const contentType = res.headers?.get ? (res.headers.get('content-type') || '') : '';
    if (res.ok && (!contentType || contentType.includes('application/json'))) {
      let status: StatusConfig = await res.json();
      if (status?.maintenance?.enabled && isMaintenanceBypassed()) {
        status = {
          ...status,
          maintenance: {
            ...status.maintenance,
            enabled: false,
          },
        };
      }
      return status;
    }
  } catch (err) {
    console.debug('Failed to fetch status.json:', err);
  }
  return null;
}

/**
 * Checks whether the backend API is reachable and responding.
 * Defaults to 10s timeout and retries once to absorb serverless cold-start wakeups.
 */
export async function checkBackendReachable(timeoutMs = 10000): Promise<boolean> {
  const tryFetch = async (timeout: number): Promise<boolean> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const res = await fetch(apiUrl('/api/health'), {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache',
      });
      clearTimeout(timer);
      return res.ok;
    } catch {
      clearTimeout(timer);
      return false;
    }
  };

  const firstOk = await tryFetch(timeoutMs);
  if (firstOk) return true;

  // Retry once in case the first attempt was during container spin-up
  await new Promise((resolve) => setTimeout(resolve, 500));
  return tryFetch(5000);
}

/**
 * Renders an announcement banner at the top of the page if configured for the given target.
 */
export function displayAnnouncementIfConfigured(
  config: StatusConfig | null,
  target: 'broadcast' | 'view'
): HTMLElement | null {
  if (!config?.announcement?.enabled) return null;
  const msg = config.announcement.message?.trim();
  if (!msg) return null;

  const confTarget = config.announcement.target || 'broadcast';
  if (confTarget !== 'all' && confTarget !== target) return null;

  // Check if previously dismissed in this session
  const storageKey = `dismissed_announcement_${btoa(unescape(encodeURIComponent(msg))).slice(0, 16)}`;
  if (sessionStorage.getItem(storageKey)) return null;

  // Remove existing announcement banner if any
  document.getElementById('system-announcement-bar')?.remove();

  const banner = document.createElement('div');
  banner.id = 'system-announcement-bar';
  banner.className = `system-announcement-bar severity-${config.announcement.severity || 'info'}`;
  banner.setAttribute('role', 'status');
  banner.innerHTML = `
    <div class="announcement-content">
      <span class="announcement-icon" aria-hidden="true">ℹ</span>
      <span class="announcement-text">${escapeHtml(msg)}</span>
    </div>
    <button type="button" class="announcement-dismiss-btn" aria-label="Dismiss notice">&times;</button>
  `;

  const dismissBtn = banner.querySelector('.announcement-dismiss-btn');
  dismissBtn?.addEventListener('click', () => {
    sessionStorage.setItem(storageKey, 'true');
    banner.remove();
  });

  document.body.prepend(banner);
  return banner;
}

/**
 * Renders a full-screen maintenance mode screen.
 */
export function renderMaintenanceView(
  container: HTMLElement,
  options?: { title?: string; message?: string }
): void {
  const title = options?.title?.trim() || 'Scheduled Maintenance';
  const msg =
    options?.message?.trim() ||
    'Fluent Church is currently undergoing scheduled maintenance. Please check back shortly.';

  container.innerHTML = `
    <div class="system-status-overlay">
      <div class="card system-status-card" role="alert">
        <div class="brand system-status-brand">
          <img src="/assets/logo.310e9178bbe7faa2c4c694ec.svg" width="130" height="26" alt="fluent.church">
        </div>
        <div class="system-status-badge badge-maintenance">Maintenance Mode</div>
        <h1 class="system-status-title">${escapeHtml(title)}</h1>
        <p class="system-status-desc">${escapeHtml(msg)}</p>
        <button type="button" class="btn btn-primary system-status-btn" onclick="window.location.reload()">
          Refresh
        </button>
      </div>
    </div>
  `;
}

/**
 * Renders a full-screen "Service Unavailable" screen when the backend cannot be reached.
 */
export function renderServiceUnavailableView(
  container: HTMLElement,
  onRetry?: () => void
): void {
  container.innerHTML = `
    <div class="system-status-overlay">
      <div class="card system-status-card" role="alert">
        <div class="brand system-status-brand">
          <img src="/assets/logo.310e9178bbe7faa2c4c694ec.svg" width="130" height="26" alt="fluent.church">
        </div>
        <div class="system-status-badge badge-unavailable">Connection Offline</div>
        <h1 class="system-status-title">Service Temporarily Unavailable</h1>
        <p class="system-status-desc">
          We're unable to connect to Fluent Church service. Please check your network connection or try again shortly.
        </p>
        <button type="button" id="system-retry-btn" class="btn btn-primary system-status-btn">
          Retry Connection
        </button>
      </div>
    </div>
  `;

  const retryBtn = container.querySelector('#system-retry-btn');
  retryBtn?.addEventListener('click', () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  });
}

